import { DestroyRef, inject, Injectable } from '@angular/core';

export type UserRole = 'Admin' | 'User' | 'OrganizationManager';
import { Router } from '@angular/router';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { catchError, filter } from 'rxjs/operators';
import { Observable, of, Subject, Subscription, tap, throwError, timer } from 'rxjs';
import { LocalStorageService } from '../local-storage.service';
import { storageTokenKeys } from '../../constants/constants';
import { TokenPair } from '../../models/token-pair';
import { AuthRepository } from './auth.repository';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApplicationInsightsService } from '../application-insights.service';
import { AccountService } from './account.service';
import { decodeTokenPayload } from './decode-token';
import { IntercomService } from '../intercom.service';
import { NotificationService } from 'ui-kit';
import { CustomErrorCode } from '../../constants/custom-error-code';


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly localStorageService = inject(LocalStorageService);
  private readonly authRepository = inject(AuthRepository);
  private readonly matDialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appInsightsService = inject(ApplicationInsightsService);
  private readonly accountService = inject(AccountService);
  private readonly intercomService = inject(IntercomService);
  private readonly notifications = inject(NotificationService);

  // Guards against duplicate notify+logout when several in-flight requests all return
  // 403 TenantDisabled at once.
  private tenantDisabledHandled = false;
  // Same guard for a session that can no longer be refreshed (LBRD-6384): every request
  // waiting on the failed refresh lands here at once.
  private sessionExpiredHandled = false;

  readonly maxTimer = 2147483647;
  readonly diffToRefreshMs = 1 * 60 * 1000;

  private readonly refreshTokenExpireAt$$ = new Subject<Date | null>();
  private expireTimer!: Subscription;
  private readonly expiredRefreshToken$$ = new Subject<Date>();
  // Fires on every path that ends the session in this tab -- an explicit `logOut()` call
  // (button, tenant-disabled, failed token refresh) and a logout that happened in another
  // tab and is only being mirrored here. Session-scoped state that must not survive into
  // the next login (e.g. `ShipmentListFilterStateService`, LBRD-5564) subscribes to this
  // instead of `AuthService` reaching into feature modules to reset them itself.
  private readonly loggedOut$$ = new Subject<void>();

  // Fires whenever a token pair is saved -- a login and every successful refresh. The tracking hub
  // does not resume on this (LBRD-6416): TrackingModeService reopens it from the new tenant's settings.
  private readonly sessionStarted$$ = new Subject<void>();

  readonly expiredRefreshToken$ = this.expiredRefreshToken$$.asObservable();
  readonly loggedOut$ = this.loggedOut$$.asObservable();
  readonly sessionStarted$ = this.sessionStarted$$.asObservable();

  isSessionExpired = false;


  constructor(
    private router: Router
  ) {
    //sync logout across tabs
    this.localStorageService.onStorageEvent()
      .pipe(
        takeUntilDestroyed()
      )
      .subscribe(event => {
        const isTokenKey = event.key === storageTokenKeys.accessToken || event.key === storageTokenKeys.refreshToken;
        const tokenRemoved = isTokenKey && event.newValue == null;
        const storageCleared = event.key === null;

        if (tokenRemoved || storageCleared) {
          this.matDialog.closeAll();
          this.router.navigate(['account/login']);
          this.loggedOut$$.next();
        }
      });

    //update timer in all tabs
    this.localStorageService.onNewValue(storageTokenKeys.refreshTokenExpiresAt)
      .pipe(
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.refreshTokenExpireAt$$.next(this.getRefreshTokenExpiresAt());
      });

    this.refreshTokenExpireAt$$
      .pipe(
        filter(value => value !== null),
        takeUntilDestroyed()
      )
      .subscribe(date => {
        this.setExpireTimer(date);
      });

    this.refreshTokenExpireAt$$.next(this.getRefreshTokenExpiresAt());
  }

  logIn(username: string, password: string) {
    return this.authRepository.logIn(username, password);
  }

  resetPassword(token: string, newPassword: string): Observable<HttpResponse<any>> {
    return this.authRepository.resetPassword(token, newPassword);
  }

  forgotPassword(email: string): Observable<HttpResponse<any>> {
    return this.authRepository.forgotPassword(email);
  }

  logOut(redirectData = {}) {
    this.appInsightsService.clearAuthenticatedUserContext();
    this.intercomService.reset();
    this.matDialog.closeAll();
    this.localStorageService.clear();
    this.router.navigate(['account/login'], redirectData);
    this.loggedOut$$.next();
  }

  // Single entry point for a tenant being disabled mid-session (from the HTTP interceptor or a
  // failed token refresh): notify once and log out. Deduped so concurrent 403s don't stack toasts.
  handleTenantDisabled() {
    if (this.tenantDisabledHandled) {
      return;
    }
    this.tenantDisabledHandled = true;
    this.notifications.error('This organization has been disabled. Please contact support.');
    this.logOut();
  }

  // The session can no longer be renewed (LBRD-6384): tell the user once and send them to Login.
  handleSessionExpired() {
    if (this.sessionExpiredHandled) {
      return;
    }
    this.sessionExpiredHandled = true;
    this.notifications.error('Your session has expired. Please sign in again.', 'Session expired');
    this.logOut();
  }

  refreshToken() {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      // Nothing to refresh with -- the session is over, don't ask the API to confirm it.
      this.handleSessionExpired();
      return throwError(() => new Error('Refresh token is not set'));
    }
    return this.authRepository.refreshTokens(refreshToken)
      .pipe(
        tap((tokenPair) => {
          this.saveUserAuthData(tokenPair.data);
        }),
        catchError((err) => {
          if (err?.error?.errorCode === CustomErrorCode.TenantDisabled) {
            this.handleTenantDisabled();
          } else if (this.isRefreshRejected(err)) {
            this.handleSessionExpired();
          }
          // Anything else (offline, 5xx) says nothing about the session itself: keep it and let
          // the caller surface the failure, rather than logging the user out over a network blip.
          return throwError(() => err);
        })
      );
  }

  /** The API refused the refresh token itself (invalid, expired or revoked). */
  private isRefreshRejected(err: unknown): boolean {
    if (!(err instanceof HttpErrorResponse)) {
      return true;
    }
    return err.status >= 400 && err.status < 500;
  }

  get isLoggedIn(): boolean {
    const authToken = this.getAccessToken();
    return authToken != null;
  }

  getAccessToken() {
    return this.localStorageService.get(storageTokenKeys.accessToken);
  }

  getRefreshToken() {
    return this.localStorageService.get(storageTokenKeys.refreshToken);
  }

  /**
   * When the access token expires: the stored value, or -- if that is missing or unreadable --
   * the token's own JWT `exp` claim (LBRD-6384). `null` only when neither can be read.
   */
  getAccessTokenExpiresAt(): Date | null {
    const stored = Number(this.localStorageService.get(storageTokenKeys.accessTokenExpiresAt));
    if (stored > 0) {
      return new Date(stored);
    }

    const token = this.getAccessToken();
    if (!token) {
      return null;
    }
    try {
      const expireAt = this.getTokenExpireAt(token);
      return Number.isNaN(expireAt.getTime()) ? null : expireAt;
    } catch {
      return null;
    }
  }

  getRefreshTokenExpiresAt() {
    const expireAt = this.localStorageService.get(storageTokenKeys.refreshTokenExpiresAt);
    return expireAt
      ? new Date(Number(expireAt))
      : null;
  }

  isAccessTokenRequiresRefresh(): boolean {
    if (!this.getAccessToken()) {
      // Not logged in -- there is no session to refresh.
      return false;
    }

    const expireAt = this.getAccessTokenExpiresAt();

    if (!expireAt) {
      // A token whose expiry can't be read (neither stored nor in a JWT `exp`) can't be
      // trusted to still be valid -- refresh it rather than send it (LBRD-6384).
      return true;
    }

    const now = new Date();
    const timeLeftMs = expireAt.getTime() - now.getTime();
    const requires = timeLeftMs <= this.diffToRefreshMs;
    return requires;
  }

  getUserRoles(): UserRole[] {
    return this.accountService.getUserRoles();
  }

  isAdmin(): boolean {
    return this.getUserRoles().includes('Admin');
  }

  isUser(): boolean {
    return this.getUserRoles().includes('User');
  }

  isTokenExpired(token: string): boolean {
    if (!token) {
      return true;
    }

    try {
      const expireAt = this.getTokenExpireAt(token);
      return expireAt.getTime() <= Date.now();
    } catch {
      return true;
    }
  }

  saveUserAuthData(tokenPair: TokenPair) {
    const refreshTokenExpiresAt = this.getTokenExpireAt(tokenPair.refreshToken);
    const accessTokenExpiresAt = this.getTokenExpireAt(tokenPair.accessToken);

    this.localStorageService.set(storageTokenKeys.accessToken, tokenPair.accessToken);
    this.localStorageService.set(storageTokenKeys.refreshToken, tokenPair.refreshToken);
    this.localStorageService.set(storageTokenKeys.refreshTokenExpiresAt, refreshTokenExpiresAt.getTime().toString());
    this.localStorageService.set(storageTokenKeys.accessTokenExpiresAt, accessTokenExpiresAt.getTime().toString());

    this.refreshTokenExpireAt$$.next(refreshTokenExpiresAt);

    // A fresh session: re-arm the one-shot notify+logout guards for its own end.
    this.sessionExpiredHandled = false;
    this.tenantDisabledHandled = false;
    this.sessionStarted$$.next();

    // Set user context in Application Insights
    const userId = this.accountService.getUserId();
    const userInfo = this.accountService.getAccountInfo();
    if (userId) {
      this.appInsightsService.setAuthenticatedUserContext(userId);
    }

    this.intercomService.identify();
  }

  private setExpireTimer(date: Date) {
    if (this.expireTimer) {
      this.expireTimer.unsubscribe();
    }

    let diffInMs = date.getTime() - Date.now();
    if (diffInMs > this.maxTimer) {
      //issue with future time https://github.com/ReactiveX/rxjs/issues/3015
      diffInMs = this.maxTimer;
    }
    this.expireTimer = timer(diffInMs)
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((x) => {
        // Clear Application Insights user context when token expires
        this.appInsightsService.clearAuthenticatedUserContext();
        this.expiredRefreshToken$$.next(date);
      });
  }

  private getTokenExpireAt(token: string): Date {
    const payload = decodeTokenPayload(token);
    return new Date((payload['exp'] as number) * 1000);
  }
}

